/**
 * Email-change: business-error model.
 *
 * As in `password-change-error.ts`, the work is not classifying the failure but
 * deciding WHICH FIELD carries it. The backend's status already says so, and its
 * messages are user-facing copy written for this form
 * (`backend/src/features/auth/application/use-cases/RequestEmailChange.ts`):
 *
 * - `401`: "Your current password is incorrect." → the password field.
 *   (Also covers "Your session is no longer valid", which is rarer; showing it on
 *   the same field is harmless, and the api client's refresh has already run.)
 * - `409`: "This email address is already in use." → the address field. The one
 *   refusal this side cannot decide: it needs to look at other accounts.
 * - `400`: "This is already your sign-in email." and the missing-field guard →
 *   the address field.
 * - anything else (403 archived, 429 rate limit, 5xx, offline) → a snackbar.
 *   Attaching those to a field would tell the user to fix an input that is fine.
 *
 * Re-using the backend's message verbatim is deliberate: rewriting it here would
 * create a second copy to keep in sync, and the server's wording is the one the
 * security review looked at.
 */
import { classifyInfraError, type InfraFailure } from '@/utils/infra-error';

import type { EmailChangeValidationErrors } from './email-change.model';

export type EmailChangeError =
  | InfraFailure
  | { kind: 'validation'; fields: EmailChangeValidationErrors };

const FALLBACK_MESSAGE = 'Failed to request the email change.';

/**
 * Map an unknown thrown error into the form's error model. Statuses the form can
 * act on become field errors; everything else stays a coarse infra failure the
 * section surfaces as a snackbar.
 */
export function toEmailChangeError(e: unknown): EmailChangeError {
  const failure = classifyInfraError(e, FALLBACK_MESSAGE);
  if (failure.kind !== 'api') return failure;

  if (failure.status === 401) {
    return { kind: 'validation', fields: { currentPassword: failure.message } };
  }
  if (failure.status === 409 || failure.status === 400) {
    return { kind: 'validation', fields: { newEmail: failure.message } };
  }
  return failure;
}
